/**
 * frontend/src/pages/valuation/_FinancialsTieOut.tsx
 *
 * VAL-READER-TIEOUT-1 - one card per fiscal year: do our figures rebuild the client's
 * printed P&L totals? Client text K1-K7 is owner-approved (2026-10-13). Nothing here
 * blocks Continue; a year that does not tie is the valuator's decision, saved by the server.
 */
import { useState } from 'react'

import type { TieoutDecisionBody, TieoutTie, TieoutYear } from '@/api/valuationFinancials'
import { FinancialsSources } from './_FinancialsSources'

const TIE_LABEL: Record<string, string> = {
  gross_profit: 'Gross profit',
  operating_income: 'Operating income',
  net_income: 'Net income',
}
const fmt = (n: number | null | undefined) =>
  typeof n === 'number' ? n.toLocaleString('en-US', { maximumFractionDigits: 2 }) : '—'
const BTN = 'rounded border border-pal-line-strong bg-pal-white px-3 py-1 text-sm text-gray-800 hover:bg-pal-surface-subtle'
const PRIMARY = 'rounded bg-blue-600 px-3 py-1 text-sm text-pal-white hover:bg-blue-700'

type Mode = 'keep' | 'enter' | null

/** WF-B-5 (owner approved 2026-10-25 11:15). */
const TYPED_YEAR_TEXT = "You typed this year's figures, so there is no statement to compare them with."

/** K4. A single printed line (no D&A taken off) drops the "less ..." clause. */
function suggestionText(t: TieoutTie): string | null {
  const s = t.suggestion
  if (!s) return null
  const shows = `${s.field_label} looks missing. Your P&L shows ${s.client_line} ${fmt(s.amount)}`
  return s.da_amount
    ? `${shows}; less depreciation and amortization ${fmt(s.da_amount)} gives ${fmt(s.result)}. Use this figure?`
    : `${shows}. Use this figure?`
}

function Decision({ year, t, onDecide }: {
  year: number
  t: TieoutTie
  onDecide: (b: TieoutDecisionBody) => void
}) {
  const [mode, setMode] = useState<Mode>(null)
  const [reason, setReason] = useState('')
  const [figure, setFigure] = useState('')
  const [field, setField] = useState(t.fields?.[0]?.field ?? '')
  const label = TIE_LABEL[t.tie] ?? t.tie
  const saved = t.decision
  if (saved) {
    const when = saved.at_central ? `${saved.at_central.replace('T', ' ').slice(0, 16)} CT` : ''
    const what = saved.decision === 'keep' ? 'Kept as is' : saved.decision === 'enter_figure' ? 'Figure entered' : 'Suggestion used'
    return (
      <p className="text-sm text-pal-ink-body">
        <span>{`${label}: ${what}${saved.reason ? `: ${saved.reason}` : ''} - ${saved.by}, ${when}`}</span>
      </p>
    )
  }
  const send = (b: Omit<TieoutDecisionBody, 'year' | 'tie'>) => onDecide({ year, tie: t.tie, ...b })
  const sentence = suggestionText(t)
  const canEnter = (t.fields ?? []).length > 0
  return (
    <div role="group" aria-label={label} className="space-y-2 rounded border border-pal-warning-border bg-pal-warning-bg p-3">
      {sentence && <p className="text-sm text-gray-800">{sentence}</p>}
      <div className="flex flex-wrap gap-2">
        {sentence && <button type="button" className={PRIMARY} onClick={() => send({ decision: 'use_suggestion' })}>Use it</button>}
        {canEnter && <button type="button" className={BTN} onClick={() => setMode('enter')}>Enter a figure</button>}
        <button type="button" className={BTN} onClick={() => setMode('keep')}>Keep as is</button>
      </div>
      {mode === 'keep' && (
        <div className="space-y-2">
          <label className="block text-sm text-gray-800">
            {`Why does FY${year} not add up? This is saved with the engagement.`}
            <textarea className="mt-1 block w-full rounded border border-pal-line-strong p-2 text-sm"
              value={reason} onChange={e => setReason(e.target.value)} />
          </label>
          <button type="button" className={PRIMARY}
            onClick={() => { if (reason.trim()) send({ decision: 'keep', reason: reason.trim() }) }}>Save</button>
        </div>
      )}
      {mode === 'enter' && (
        <div className="space-y-2">
          {t.fields.length > 1 && (
            <select aria-label="Field" className="rounded border border-pal-line-strong p-1 text-sm"
              value={field} onChange={e => setField(e.target.value)}>
              {t.fields.map(f => <option key={f.field} value={f.field}>{f.label}</option>)}
            </select>
          )}
          <label className="block text-sm text-gray-800">
            {t.fields.find(f => f.field === field)?.label ?? field}
            <input type="number" className="mt-1 block rounded border border-pal-line-strong p-1 text-sm"
              value={figure} onChange={e => setFigure(e.target.value)} />
          </label>
          <button type="button" className={PRIMARY}
            onClick={() => {
              const v = Number(figure)
              if (figure.trim() !== '' && Number.isFinite(v) && v >= 0) send({ decision: 'enter_figure', field, value: v })
            }}>Save</button>
        </div>
      )}
    </div>
  )
}

export function FinancialsTieOut({ data, onDecide }: {
  data: TieoutYear
  onDecide: (b: TieoutDecisionBody) => void
}) {
  const [showLines, setShowLines] = useState(false)
  const failing = data.ties.filter(t => t.status === 'differs')
  // WALKFIX-B F17: no statement lines for the year means its figures were typed - nothing to compare.
  const typed = data.lines.length === 0
  return (
    <section className="space-y-3 rounded-lg border border-pal-line p-4">
      <h2 className="text-sm font-semibold text-pal-ink">{`Does FY${data.year} add up to your P&L?`}</h2>
      {data.sources && <FinancialsSources year={data.year} sources={data.sources} />}
      {typed ? (
        <p className="text-sm text-pal-ink-body">{TYPED_YEAR_TEXT}</p>
      ) : (<>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-pal-ink-muted">
            <th className="font-normal" />
            <th className="font-normal">Our figures</th>
            <th className="font-normal">Your P&amp;L</th>
            <th className="font-normal">Difference</th>
          </tr>
        </thead>
        <tbody>
          {data.ties.map(t => (
            <tr key={t.tie}>
              <td>{TIE_LABEL[t.tie] ?? t.tie}</td>
              <td>{fmt(t.ours)}</td>
              {t.status === 'not_printed' ? (
                <td colSpan={2} className="text-pal-ink-muted">Your P&amp;L does not print this total.</td>
              ) : (
                <>
                  <td>{fmt(t.printed)}</td>
                  <td>{t.status === 'ties' ? <span aria-label="ties" className="text-green-700">✓</span> : fmt(t.difference)}</td>
                </>
              )}
            </tr>
          ))}
          <tr className="text-pal-ink-soft">
            <td>Other income (not in the valuation)</td>
            <td />
            <td>{fmt(data.other_income?.printed)}</td>
            <td />
          </tr>
        </tbody>
      </table>
      {failing.map(t => <Decision key={t.tie} year={data.year} t={t} onDecide={onDecide} />)}
      <button type="button" className={BTN} onClick={() => setShowLines(s => !s)}>Show your P&amp;L lines</button>
      {showLines && (
        <ul role="list" aria-label="Your P&L lines" className="text-sm text-pal-ink-body">
          {data.lines.map((l, i) => (
            <li key={i}>
              <span>{l.source_label}</span>
              {` ${fmt(l.value)}${l.mapped_to ? ` → ${l.mapped_to}` : ''}`}
            </li>
          ))}
        </ul>
      )}
      </>)}
    </section>
  )
}
